"use client";
import { useMemo, useState } from "react";
import { Droplets } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useRooms } from "@/components/calculators/room-context";
import {
  toNum,
  fmt,
  DEHU_CATALOG,
  NAM_UNITS,
  DEHU_CLASS_FACTOR,
} from "@/components/calculators/calc-constants";
import { NumField, EmailButton } from "@/components/calculators/calc-ui";

/* ── IICRC S500 water source → category lookup ── */
type WaterSource =
  | "supply-line"
  | "appliance-overflow"
  | "toilet-tank"
  | "toilet-bowl"
  | "dishwasher-grey"
  | "washing-machine"
  | "aquarium"
  | "sink-overflow"
  | "sewage-backup"
  | "floodwater"
  | "river-stream"
  | "storm-water";

type Category = "1" | "2" | "3";

const SOURCE_MAP: Record<WaterSource, { label: string; category: Category }> = {
  "supply-line":         { label: "Clean supply line break",               category: "1" },
  "appliance-overflow":  { label: "Appliance overflow (dishwasher/fridge)", category: "1" },
  "toilet-tank":         { label: "Toilet tank overflow",                   category: "1" },
  "toilet-bowl":         { label: "Toilet bowl overflow (no feces)",        category: "2" },
  "dishwasher-grey":     { label: "Dishwasher grey water",                  category: "2" },
  "washing-machine":     { label: "Washing machine grey water",             category: "2" },
  "aquarium":            { label: "Aquarium leak",                          category: "2" },
  "sink-overflow":       { label: "Sink overflow",                          category: "2" },
  "sewage-backup":       { label: "Sewage backup",                          category: "3" },
  "floodwater":          { label: "Floodwater intrusion",                   category: "3" },
  "river-stream":        { label: "River / stream overflow",                category: "3" },
  "storm-water":         { label: "Storm water",                            category: "3" },
};

const CATEGORY_COLOR: Record<Category, string> = {
  "1": "#4ade80",
  "2": "#D4A84B",
  "3": "#f87171",
};

const CATEGORY_RATIONALE: Record<Category, string> = {
  "1": "IICRC S500 Category 1 — water originates from a sanitary source and poses no substantial risk from ingestion or skin contact.",
  "2": "IICRC S500 Category 2 — water contains significant contamination and has potential to cause discomfort or sickness if contacted.",
  "3": "IICRC S500 Category 3 — grossly contaminated; contains pathogenic, toxigenic, or other harmful agents. Full PPE and containment required.",
};

/* ── Class suggestion logic ── */
function suggestClass(summary: ReturnType<typeof useRooms>["summary"], rooms: ReturnType<typeof useRooms>["rooms"]): string {
  const hasFloor = summary.totalFloorSqFt > 0;
  const hasWalls = summary.totalWallSqFt > 0;
  const hasCeiling = summary.totalCeilingSqFt > 0;
  const hasSpecialty = rooms.some((r) => r.materialType === "hardwood" || r.materialType === "concrete");

  if (hasSpecialty) return "4";
  if (hasFloor && hasWalls && hasCeiling) return "3";
  if (hasFloor && (hasWalls || summary.totalFloorSqFt > 100)) return "2";
  if (hasFloor && summary.totalFloorSqFt < 100) return "1";
  return "2";
}

const BASELINE_DRY_DAYS: Record<string, number> = { "1": 3, "2": 4, "3": 6, "4": 10 };

export function WaterDamageTab() {
  const { summary, rooms } = useRooms();
  const [source, setSource] = useState<WaterSource>("supply-line");
  const [classOfLoss, setClassOfLoss] = useState<string>(() => suggestClass(summary, rooms));
  const [dehuId, setDehuId] = useState(DEHU_CATALOG[6].id);
  const [namCfm, setNamCfm] = useState("500");
  const [targetAch, setTargetAch] = useState("4");
  const [installedCapacity, setInstalledCapacity] = useState("");

  /* Manual loss dimensions. Every figure on this tab used to come from the
   * Job Setup room list and nowhere else, so with no rooms the calculator
   * rendered in full and answered 0 air movers, 0 dehumidifiers, 0 drying
   * days — confident, authoritative and meaningless. That is why the whole
   * tab section used to be hidden until a room existed. The gate is gone, so
   * the fallback has to be real: same manual-override pattern as
   * mold-remediation.tsx, which has had one all along. */
  const [manualL, setManualL] = useState("");
  const [manualW, setManualW] = useState("");
  const [manualH, setManualH] = useState("");

  const category = SOURCE_MAP[source].category;
  const dehu = DEHU_CATALOG.find((d) => d.id === dehuId) ?? DEHU_CATALOG[0];

  const suggested = useMemo(() => suggestClass(summary, rooms), [summary, rooms]);

  /* Manual dimensions win when both plan dimensions are given; otherwise the
   * room summary stands.
   *
   * Floor area and volume follow from the dimensions. Wet WALL and CEILING
   * area deliberately do not: which surfaces a loss actually reached is a
   * judgement, and Job Setup asks for it explicitly (AffectedSurface). An
   * earlier draft here assumed a manual room was wet floor-to-ceiling, which
   * tripled the air-mover recommendation on this example — 13–17 units where
   * the floor alone justifies 5–6 — while the caption underneath still read
   * "0 ft² walls". Over-recommending equipment off dimensions nobody said
   * were wet is the worse error, so the manual path sizes from the floor and
   * the volume, and marking surfaces in Job Setup is how you add the rest.
   *
   * Standing water has no dimensional equivalent and only comes from rooms. */
  const dims = useMemo(() => {
    const L = toNum(manualL), W = toNum(manualW), H = toNum(manualH);
    const manualFloor = L > 0 && W > 0 ? L * W : 0;
    if (manualFloor > 0) {
      return {
        manual: true,
        floorArea: manualFloor,
        ceilArea: 0,
        wallArea: 0,
        volume: H > 0 ? manualFloor * H : 0,
        standingGal: summary.totalStandingGal,
      };
    }
    return {
      manual: false,
      floorArea: summary.totalFloorSqFt,
      ceilArea: summary.totalCeilingSqFt,
      wallArea: summary.totalWallSqFt,
      volume: summary.totalVolumeFt3,
      standingGal: summary.totalStandingGal,
    };
  }, [manualL, manualW, manualH, summary]);

  /* ── Equipment computations from the effective dimensions ── */
  const equip = useMemo(() => {
    const { floorArea, wallArea, ceilArea, volume, standingGal } = dims;

    /* Air movers — IICRC S500 §10.6.4.2.
     *   Floor: 1 AM per 50–70 sqft wet floor.
     *   Wall:  S500 specifies "1 AM per 10–16 linear ft of wet wall".
     *          At a typical 8' ceiling that is 80–128 sqft of wet wall.
     *          (Previously used 100–150 sqft, which under-recommended
     *          ~10–20% on standard-height rooms.)
     *   Ceiling: same LF heuristic applied to wet ceiling. */
    const amFloor = floorArea > 0 ? { min: Math.ceil(floorArea / 70), max: Math.ceil(floorArea / 50) } : { min: 0, max: 0 };
    const amWall = wallArea > 0 ? { min: Math.ceil(wallArea / 128), max: Math.ceil(wallArea / 80) } : { min: 0, max: 0 };
    const amCeil = ceilArea > 0 ? { min: Math.ceil(ceilArea / 128), max: Math.ceil(ceilArea / 80) } : { min: 0, max: 0 };
    const amMin = amFloor.min + amWall.min + amCeil.min;
    const amMax = amFloor.max + amWall.max + amCeil.max;

    /* Dehu */
    const factor = DEHU_CLASS_FACTOR[classOfLoss] ?? 0.2;
    const pintsNeeded = volume * factor;
    const dehuCount = dehu.aham > 0 && pintsNeeded > 0 ? Math.ceil(pintsNeeded / dehu.aham) : 0;

    /* ACH / NAM */
    const ach = toNum(targetAch) || 4;
    const reqCfm = (volume * ach) / 60;
    const namCount = toNum(namCfm) > 0 ? Math.ceil(reqCfm / toNum(namCfm)) : 0;

    /* Drying time */
    const required = pintsNeeded;
    const installed = toNum(installedCapacity) || (dehuCount * dehu.aham);
    const baseline = BASELINE_DRY_DAYS[classOfLoss] ?? 4;
    let estDays = 0;
    if (required > 0 && installed > 0) {
      const r = installed / required;
      let d = baseline / r;
      if (r >= 1) d = baseline / Math.min(1.5, Math.sqrt(r));
      estDays = Math.max(1, Math.round(d));
    }

    return { amMin, amMax, pintsNeeded, dehuCount, reqCfm, namCount, estDays, volume, floorArea, standingGal };
  }, [dims, classOfLoss, dehu, targetAch, namCfm, installedCapacity]);

  const panelStyle = { background: "rgba(10,20,36,0.96)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,0.22)", borderRadius: 14, padding: "20px 22px", marginBottom: 24, boxShadow: "0 4px 24px rgba(0,0,0,0.55)" };
  const sectionTitle = { fontSize: 13, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" as const, color: "#D4A84B", marginBottom: 14 };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>

      {/* Loss dimensions — the fallback that lets this tab work before any
          room exists. Identical in shape to the Mold tab's panel. */}
      <div style={panelStyle}>
        <p style={sectionTitle}>Affected Area</p>
        <p style={{ fontSize: 12, color: "rgba(255,255,255,0.70)", marginBottom: 12 }}>
          {dims.manual
            ? `Using the dimensions below: ${fmt(dims.floorArea)} ft² floor · ${fmt(dims.volume)} ft³. Wet walls and ceiling are not assumed — mark the affected surfaces in Job Setup to include them.`
            : dims.floorArea > 0
              ? `Using room summary: ${fmt(dims.floorArea)} ft² · ${fmt(dims.volume)} ft³. Enter dimensions below to override.`
              : "No rooms added — enter the affected dimensions to size equipment, or add rooms in Job Setup above."}
        </p>
        <div className="grid grid-cols-3 gap-3">
          <NumField id="wd-l" label="Length (ft)" value={manualL} onChange={setManualL} placeholder="auto" />
          <NumField id="wd-w" label="Width (ft)" value={manualW} onChange={setManualW} placeholder="auto" />
          <NumField id="wd-h" label="Ceiling height (ft)" value={manualH} onChange={setManualH} placeholder="auto" />
        </div>
        <div style={{ marginTop: 12, display: "flex", gap: 20 }}>
          <div>
            <span style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", textTransform: "uppercase" }}>Floor area</span>
            <p style={{ fontSize: 22, fontWeight: 700, color: "#ffffff", fontVariantNumeric: "tabular-nums" }}>{dims.floorArea ? fmt(dims.floorArea) : "—"} ft²</p>
          </div>
          <div>
            <span style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", textTransform: "uppercase" }}>Volume</span>
            <p style={{ fontSize: 22, fontWeight: 700, color: "#ffffff", fontVariantNumeric: "tabular-nums" }}>{dims.volume ? fmt(dims.volume) : "—"} ft³</p>
          </div>
        </div>
      </div>

      {/* Section A — Water Source & Category */}
      <div style={panelStyle}>
        <p style={sectionTitle}>A — Water Source & Category</p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, alignItems: "start" }}>
          <div className="space-y-1.5">
            <Label htmlFor="wd-source" style={{ color: "rgba(255,255,255,0.85)" }}>Water source</Label>
            <Select value={source} onValueChange={(v) => setSource(v as WaterSource)}>
              <SelectTrigger id="wd-source" className="h-10 w-full"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="supply-line">Clean supply line break</SelectItem>
                <SelectItem value="appliance-overflow">Appliance overflow</SelectItem>
                <SelectItem value="toilet-tank">Toilet tank overflow</SelectItem>
                <SelectItem value="toilet-bowl">Toilet bowl overflow (no feces)</SelectItem>
                <SelectItem value="dishwasher-grey">Dishwasher grey water</SelectItem>
                <SelectItem value="washing-machine">Washing machine grey water</SelectItem>
                <SelectItem value="aquarium">Aquarium leak</SelectItem>
                <SelectItem value="sink-overflow">Sink overflow</SelectItem>
                <SelectItem value="sewage-backup">Sewage backup</SelectItem>
                <SelectItem value="floodwater">Floodwater intrusion</SelectItem>
                <SelectItem value="river-stream">River / stream overflow</SelectItem>
                <SelectItem value="storm-water">Storm water</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div style={{ background: "rgba(255,255,255,0.06)", border: `1px solid ${CATEGORY_COLOR[category]}40`, borderRadius: 10, padding: "14px 16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <Droplets size={16} style={{ color: CATEGORY_COLOR[category] }} />
              <span style={{ fontSize: 13, fontWeight: 700, color: CATEGORY_COLOR[category] }}>
                Category {category} — {category === "1" ? "Clean Water" : category === "2" ? "Grey Water" : "Black Water"}
              </span>
            </div>
            <p style={{ fontSize: 12, color: "rgba(255,255,255,0.75)", lineHeight: 1.6 }}>{CATEGORY_RATIONALE[category]}</p>
          </div>
        </div>
      </div>

      {/* Section B — Classification */}
      <div style={panelStyle}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <p style={{ ...sectionTitle, marginBottom: 0 }}>B — Drying Class</p>
          {suggested !== classOfLoss && (
            <button type="button" onClick={() => setClassOfLoss(suggested)} style={{ fontSize: 11, color: "#D4A84B", background: "transparent", border: "1px solid rgba(212,168,75,0.4)", borderRadius: 6, padding: "4px 10px", cursor: "pointer" }}>
              Use suggested (Class {suggested})
            </button>
          )}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6, marginBottom: 10 }}>
          {(["1", "2", "3", "4"] as const).map((c) => (
            <button key={c} type="button" onClick={() => setClassOfLoss(c)} aria-pressed={classOfLoss === c}
              style={{ padding: "10px 8px", borderRadius: 8, border: "1px solid " + (classOfLoss === c ? "#D4A84B" : "rgba(255,255,255,0.18)"), background: classOfLoss === c ? "#B88A2F" : "transparent", color: classOfLoss === c ? "#080F1A" : "rgba(255,255,255,0.85)", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
              Class {c}
            </button>
          ))}
        </div>
        <p style={{ fontSize: 11, color: "rgba(255,255,255,0.70)" }}>
          {classOfLoss === "1" && "Class 1 — least absorbed. Limited area, low-porosity materials. Factor: 0.10 pints/ft³/day."}
          {classOfLoss === "2" && "Class 2 — significant absorption. Full room carpet/walls wet. Factor: 0.20 pints/ft³/day."}
          {classOfLoss === "3" && "Class 3 — greatest absorption. Ceiling, walls, floor all saturated. Factor: 0.30 pints/ft³/day."}
          {classOfLoss === "4" && "Class 4 — specialty drying. Hardwood, concrete, plaster. Very slow evaporation. Factor: 0.40 pints/ft³/day."}
          {classOfLoss === "4" && rooms.some((r) => r.materialType === "hardwood" || r.materialType === "concrete") && " ⚠ Specialty material detected in rooms — Class 4 confirmed."}
        </p>
      </div>

      {/* Section C — Equipment */}
      <div style={panelStyle}>
        <p style={sectionTitle}>C — Equipment Sizing</p>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Air Movers */}
          <div style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 12, padding: "16px 18px" }}>
            <p style={{ fontSize: 12, fontWeight: 700, color: "#D4A84B", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Air Movers (IICRC S500)</p>
            <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
              <div>
                <span style={{ fontSize: 10, color: "rgba(255,255,255,0.6)", textTransform: "uppercase" }}>Min</span>
                <p style={{ fontSize: 36, fontWeight: 700, color: "#ffffff", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{equip.amMin || "—"}</p>
              </div>
              <span style={{ fontSize: 20, color: "rgba(255,255,255,0.4)" }}>→</span>
              <div>
                <span style={{ fontSize: 10, color: "rgba(255,255,255,0.6)", textTransform: "uppercase" }}>Max</span>
                <p style={{ fontSize: 36, fontWeight: 700, color: "#D4A84B", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{equip.amMax || "—"}</p>
              </div>
            </div>
            <p style={{ fontSize: 11, color: "rgba(255,255,255,0.60)", marginTop: 8 }}>Based on {fmt(equip.floorArea)} ft² floor + {fmt(summary.totalWallSqFt)} ft² walls + {fmt(summary.totalCeilingSqFt)} ft² ceiling</p>
          </div>

          {/* Dehu */}
          <div style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 12, padding: "16px 18px" }}>
            <p style={{ fontSize: 12, fontWeight: 700, color: "#D4A84B", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Dehumidifiers</p>
            <div className="space-y-1.5" style={{ marginBottom: 10 }}>
              <Label htmlFor="wd-dehu" style={{ color: "rgba(255,255,255,0.85)", fontSize: 12 }}>Unit</Label>
              <Select value={dehuId} onValueChange={(v) => setDehuId(v ?? "")}>
                <SelectTrigger id="wd-dehu" className="h-9 w-full"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DEHU_CATALOG.map((d) => <SelectItem key={d.id} value={d.id}>{d.label} — {d.aham} pts/day</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
              <p style={{ fontSize: 36, fontWeight: 700, color: "#D4A84B", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{equip.dehuCount || "—"}</p>
              <span style={{ fontSize: 12, color: "rgba(255,255,255,0.6)" }}>units · {fmt(equip.pintsNeeded)} pts/day needed</span>
            </div>
          </div>

          {/* ACH / NAM */}
          <div style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 12, padding: "16px 18px" }}>
            <p style={{ fontSize: 12, fontWeight: 700, color: "#D4A84B", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Air Filtration (ACH)</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
              <div className="space-y-1.5">
                <Label htmlFor="wd-ach" style={{ color: "rgba(255,255,255,0.85)", fontSize: 12 }}>Target ACH</Label>
                <Select value={targetAch} onValueChange={(v) => setTargetAch(v ?? "")}>
                  <SelectTrigger id="wd-ach" className="h-9 w-full"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="4">4 ACH — baseline</SelectItem>
                    <SelectItem value="6">6 ACH — elevated</SelectItem>
                    <SelectItem value="8">8 ACH — aggressive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="wd-nam" style={{ color: "rgba(255,255,255,0.85)", fontSize: 12 }}>NAM unit</Label>
                <Select value={namCfm} onValueChange={(v) => setNamCfm(v ?? "")}>
                  <SelectTrigger id="wd-nam" className="h-9 w-full"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {NAM_UNITS.map((u) => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
              <p style={{ fontSize: 36, fontWeight: 700, color: "#ffffff", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{equip.namCount || "—"}</p>
              <span style={{ fontSize: 12, color: "rgba(255,255,255,0.6)" }}>NAMs · {fmt(equip.reqCfm)} CFM needed</span>
            </div>
          </div>

          {/* Drying time */}
          <div style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 12, padding: "16px 18px" }}>
            <p style={{ fontSize: 12, fontWeight: 700, color: "#D4A84B", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Drying Time Estimate</p>
            <NumField id="wd-cap" label="Installed dehu capacity (pts/day) — leave blank to use selected dehu" value={installedCapacity} onChange={setInstalledCapacity} placeholder={String(equip.dehuCount * dehu.aham)} />
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 10 }}>
              <p style={{ fontSize: 36, fontWeight: 700, color: equip.estDays > 0 ? "#ffffff" : "rgba(255,255,255,0.4)", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{equip.estDays || "—"}</p>
              <span style={{ fontSize: 12, color: "rgba(255,255,255,0.6)" }}>days (estimate only)</span>
            </div>
          </div>
        </div>

        {/* Email */}
        <div style={{ marginTop: 20 }}>
          <EmailButton summary={{
            calcLabel: "Water Damage Assessment",
            rows: [
              { label: "Source", value: SOURCE_MAP[source].label },
              { label: "Category", value: `Category ${category}` },
              { label: "Class of Loss", value: `Class ${classOfLoss}` },
              { label: "Floor area", value: `${fmt(equip.floorArea)} ft²` },
              { label: "Volume", value: `${fmt(equip.volume)} ft³` },
              { label: "Standing water", value: `${fmt(equip.standingGal, 1)} gal` },
              { label: "Air Movers", value: `${equip.amMin}–${equip.amMax}` },
              { label: "AHAM needed", value: `${fmt(equip.pintsNeeded)} pts/day` },
              { label: "Dehumidifier", value: `${dehu.label} × ${equip.dehuCount}` },
              { label: "NAMs", value: `${equip.namCount} × ${namCfm} CFM` },
              { label: "Estimated drying", value: equip.estDays ? `${equip.estDays} days` : "—" },
            ],
          }} />
        </div>
      </div>
    </div>
  );
}
